import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { Box, TextField } from '@material-ui/core';
import { getHierarchy } from '../../../redux/actions/hierarchy';
import { getFranchiseDefaultReportingUnit } from '../../../redux/selectors/userSelectors';
import { TreeSelector } from '../../TreeSelector';
import { useTranslation } from 'react-i18next';

function ParseNodeChildren(childrenObj) {
  const nodes = Object.entries(childrenObj).map(([key, node]: any[]) => {
    const { children, ...rest } = node;
    const hasChildren = !!Object.entries(children).length;
    return {
      ...rest,
      hasChildren,
      ...(hasChildren && { children: ParseNodeChildren(children) }),
    };
  });
  return nodes;
}
function ParseNode(node) {
  return {
    path: node.path,
    number: node.relatedNumber,
    text: node.displayText,
    type: node.nodeTypeName,
    metas: {
      ...(node.nodeTypeName === 'Restaurant' && {
        restaurantId: Number(node.restaurantId),
      }),
    },
  };
}

/**
 * Selector of the Restaurant.
 * @param value
 * @param onChange
 * @constructor
 */
export function ShowRestaurantLists({
  value,
  onChange = (values: any[]) => { },
}: any) {
  const { t } = useTranslation();
  const dispatch: any = useDispatch();

  const refTreeSelector = useRef<any>(null);

  const [selectedReportingUnit, setSelectedReportingUnit] = useState<any>({});
  const [filterValue, setFilterValue] = useState('');
  const [filterDelayedValue, setFilterDelayedValue] = useState('');
  const [filterTimer, setFilterTimer] = useState<any>(null);

  const selectedBrand = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );
  const defaultReportingUnit = useSelector(getFranchiseDefaultReportingUnit);
  const { hierarchy } = useSelector(
    (state: RootStateOrAny) => state.HierarchyReducer,
  );

  const hierarchyNodes = useMemo(() => {
    let items = hierarchy;
    if (
      items.findIndex(item => item.id === defaultReportingUnit.nodeId) === -1
    ) {
      const { nodeId, name, relatedNumber } = defaultReportingUnit;
      items = [
        {
          id: nodeId,
          path: `/${nodeId}/`,
          rootId: nodeId,
          name,
          relatedNumber,
          level: 1,
          parentId: null,
          nodeTypeId: 1,
          nodeTypeName: t('home.assignments.reportingUnit'),
          nodeTypeCode: 'HNT_RU',
        },
        ...hierarchy,
      ];
    }
    return items.map(node => ({
      ...node,
      displayText: `${node.relatedNumber?.length ? `#     ${node.relatedNumber} | ` : ''
        }${node.name}`,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hierarchy, defaultReportingUnit]);

  const restaurantsNodes = useMemo(() => {
    let nodes = null;
    if (hierarchyNodes?.length && selectedReportingUnit?.id) {
      const { id: reportingUnitId } = selectedReportingUnit;
      let ruNodes = hierarchyNodes.filter(node =>
        node.path.startsWith(`/${reportingUnitId}/`),
      );
      const nodesObj = ruNodes.reduce(
        (acc, node) => {
          let parent = acc;
          const pathParts = node.path
            .split('/')
            .filter(part => part?.length > 0 && ruNodes.find(ruNode => ruNode.id === parseInt(part)));

          pathParts.forEach(part => {
            if (!parent.children[part] ) {
              parent.children = {
                ...parent.children,
                [part]: {
                  hasChildren: false,
                  text: '',
                  path: `${parent.path}${part}/`,
                  type: 'Reporting Group',
                  children: {},
                },
              };
            }
            parent.children[part] = {
              ...parent.children[part],
              ...((parseInt(part) === node.parentId) && {
                hasChildren: true,
              }),
              ...(parseInt(part) === node.id  && ParseNode(node)),
            };
            parent = parent.children[part];

          });

          return acc;
        },
        { hasChildren: false, children: {} },
      );

      nodes = ParseNodeChildren(nodesObj.children);
    }
    return nodes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hierarchyNodes, selectedReportingUnit, filterDelayedValue]);

  const isLoadingRestaurants = useMemo(
    () => !restaurantsNodes,
    [restaurantsNodes],
  );

  const innerValue = useMemo(() => {
    const getSelection = nodes =>
      (nodes || []).reduce((acc, node) => {
        const { children, hasChildren, ...flatNode } = node;

        let childrenAcc = [];
        if (hasChildren) childrenAcc = getSelection(children);

        const fnMatch = lookItem =>
          fnCollectionMatch(value, lookItem) ||
          fnCollectionMatch(childrenAcc, lookItem);

        const fnCollectionMatch = (collection, lookItem) =>
          collection.findIndex(item => item.path === lookItem.path) >= 0;

        if (
          fnMatch(node) ||
          (hasChildren && children.every(child => fnMatch(child)))
        ) {
          acc = [...acc, flatNode];
        } else {
          acc = [...acc, ...childrenAcc];
        }

        return acc;
      }, []);

    return getSelection(restaurantsNodes);
  }, [value, restaurantsNodes]);

  useEffect(() => {
    if (dispatch && selectedBrand) dispatch(getHierarchy(selectedBrand));
  }, [selectedBrand, dispatch]);

  useEffect(() => {
    if (!selectedReportingUnit?.id) {
      const { nodeId, name, relatedNumber } = defaultReportingUnit;
      setSelectedReportingUnit({
        id: nodeId,
        number: relatedNumber,
        name: name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultReportingUnit]);

  useEffect(() => {
    clearTimeout(filterTimer);
    setFilterTimer(
      setTimeout(() => {
        setFilterDelayedValue(filterValue);
      }, 500),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue]);

  useEffect(() => {
    if (!!filterDelayedValue?.length) refTreeSelector?.current?.expandAll();
  }, [filterDelayedValue]);

  const handleFilterChange = ({ target }) => {
    setFilterValue(target.value);
  };
  const handleNodeSelectionChange = (values: any[]) => {
    const selectedRestaurants = hierarchyNodes.filter(
      node =>
        node.nodeTypeName === 'Restaurant' &&
        values.some(value => node.path.startsWith(value.path)),
    );
    const normalizedSelection = selectedRestaurants.map(restaurant =>
      ParseNode(restaurant),
    );
    onChange(normalizedSelection);
  };
  const handleDisableCheckOnFiltering = (node: any) => {
    return !!filterDelayedValue.length && node.type !== 'Restaurant';
  };

  return (
    <Box>
      <Box
        style={{
          padding: '5px 10px',
        }}
      >
        <TextField
          size="small"
          variant="outlined"
          label={t('productGroups.assignment.filterByRestaurant')}
          fullWidth
          value={filterValue}
          name="restaurantFilter"
          onChange={handleFilterChange}
          autoFocus={false}
          disabled={isLoadingRestaurants}
          style={{
            marginTop: '8px',
          }}
        />
      </Box>
      <Box flexGrow={1}>
        <TreeSelector
          ref={refTreeSelector}
          nodes={restaurantsNodes}
          value={innerValue}
          watchForChange={[selectedReportingUnit?.name]}
          pagination={{ steps: 10 }}
          showLoadingOnRoot={true}
          showNoDataOnRoot={true}
          filterText={filterDelayedValue}
          checkDisabled={handleDisableCheckOnFiltering}
          onChange={handleNodeSelectionChange}
        />
      </Box>
    </Box>
  );
}
